import {IssueRelativeTime} from '@github-ui/ago'
import {MarkdownViewer} from '@github-ui/markdown-viewer'
import {sanitize, type SanitizedHTML} from '@github-ui/sanitize'
import {ScrollableRegion} from '@primer/react/experimental'
import {useCallback, useMemo, useState} from 'react'

import type {ScribeArtifactVersion} from '../types'
import {ArtifactHistoryMenu} from './ArtifactHistoryMenu'
import styles from './ArtifactPanelContent.module.css'

export type ArtifactType = 'plan' | 'link' | 'generic'

export type PanelArtifact = {
  id: string
  number?: number
  type: ArtifactType
  name: string
  body: string
  bodyHTML: string
  trustedBodyHTML?: SanitizedHTML
  createdAt?: string
  updatedAt?: string
  hasEdits?: boolean
  updatedByActor?: {
    login: string
  }
  resourceType?: 'issue'
  resourceId?: string | number
}

type ArtifactPanelBodyProps = {
  artifact: PanelArtifact
}

/** Just the rendered markdown (or plain-text fallback) for an artifact. */
export function ArtifactPanelBody({artifact}: ArtifactPanelBodyProps) {
  const [selection, setSelection] = useState<{artifactId: string; version: ScribeArtifactVersion} | null>(null)
  const selectedVersion = selection?.artifactId === artifact.id ? selection.version : null
  const historyResource =
    artifact.number !== undefined &&
    artifact.resourceType !== undefined &&
    artifact.resourceId !== undefined &&
    artifact.hasEdits === true
      ? {
          number: artifact.number,
          resourceType: artifact.resourceType,
          resourceId: artifact.resourceId,
        }
      : null

  const handleVersionSelect = useCallback(
    (version: ScribeArtifactVersion) => setSelection({artifactId: artifact.id, version}),
    [artifact.id],
  )
  const displayedBody = selectedVersion ? (selectedVersion.body ?? '') : artifact.body
  const displayedBodyHTML = selectedVersion ? (selectedVersion.body_html ?? '') : artifact.bodyHTML
  const trustedBodyHTML = selectedVersion ? (selectedVersion.body_html ?? undefined) : artifact.trustedBodyHTML
  const displayedAt = selectedVersion ? (selectedVersion.created_at ?? undefined) : artifact.updatedAt
  const displayedActor = selectedVersion ? (selectedVersion.actor ?? undefined) : artifact.updatedByActor
  const sanitizedHTML = useMemo(
    () => trustedBodyHTML ?? (displayedBodyHTML ? sanitize(displayedBodyHTML) : ''),
    [displayedBodyHTML, trustedBodyHTML],
  )

  return (
    <ScrollableRegion key={artifact.id} className={styles.Body} aria-label={artifact.name}>
      {(displayedAt || historyResource) && (
        <div className={styles.Metadata}>
          {displayedAt && (
            <span className={styles.MetadataText}>
              <span>{selectedVersion ? 'Edited' : 'Updated'}</span> <IssueRelativeTime datetime={displayedAt} />
              {displayedActor && <span> by {displayedActor.login}</span>}
            </span>
          )}
          {historyResource && (
            <ArtifactHistoryMenu
              {...historyResource}
              selectedVersion={selectedVersion}
              onSelect={handleVersionSelect}
            />
          )}
        </div>
      )}
      {sanitizedHTML ? (
        <div className={styles.MarkdownContent}>
          <MarkdownViewer verifiedHTML={sanitizedHTML} />
        </div>
      ) : (
        <pre className={styles.PlainBody}>{displayedBody}</pre>
      )}
    </ScrollableRegion>
  )
}
